import { UploadType } from 'components/TemplateEditor/slateTypes';
import { CustomSlateNodeType } from 'components/TemplateEditor/types';
import { withSelectionGuard } from 'components/TemplateEditor/lySlatePlugins/slatePlugins/withSelectionGuard';
import { useMemo } from 'react';
import isEqual from 'react-fast-compare';
import { Element, Node, Text, Transforms, createEditor } from 'slate';
import { withHistory } from 'slate-history';
import { ReactEditor, useSlateStatic, withReact } from 'slate-react';

export function withUpload<T extends ReactEditor>(editor: T) {
  const { isVoid, isInline, normalizeNode } = editor;

  editor.isVoid = (element) => element.type === CustomSlateNodeType.UPLOAD || isVoid(element);

  editor.isInline = (element) => element.type !== CustomSlateNodeType.UPLOAD && isInline(element);

  editor.normalizeNode = (entry) => {
    const [node, path] = entry;

    if (!Element.isElement(node) || node.type !== CustomSlateNodeType.UPLOAD) {
      return normalizeNode(entry);
    }

    const prev = path[0] - 1;
    let next = path[0] + 1;

    // 如果上传是第一个元素,在上传前面添加一个空块
    if (!Node.has(editor, [prev])) {
      Transforms.insertNodes(
        editor,
        {
          type: CustomSlateNodeType.PARAGRAPH,
          children: [{ text: '' }],
        },
        {
          at: [0],
        },
      );

      next += 1;
    }

    // 如果上传是最后一个元素,那么后面需要加上一个空块
    if (!Node.has(editor, [next])) {
      Transforms.insertNodes(
        editor,
        {
          type: CustomSlateNodeType.PARAGRAPH,
          children: [{ text: '' }],
        },
        {
          at: [next],
        },
      );
    }
  };

  return Object.assign(editor, {
    insertUpload: (uploadType: UploadType, id: string) => {
      const {
        anchor: { path: anchorPath = undefined } = {},
        focus: { path: focusPath = undefined } = {},
      } = editor.selection || {};

      if (anchorPath && focusPath && isEqual(anchorPath, focusPath)) {
        const curNode = editor.children[anchorPath[0]];

        if (curNode.type === CustomSlateNodeType.PARAGRAPH && curNode.children.length <= 1) {
          if (Text.isText(curNode.children[0]) && curNode.children[0].text.length === 0) {
            Transforms.move(editor, {
              distance: 1,
              unit: 'offset',
              reverse: true,
            });
          }
        }
      }

      Transforms.insertNodes(editor, {
        type: CustomSlateNodeType.UPLOAD,
        uploadType,
        id,
        children: [{ text: '' }],
      });
    },
  });
}

export function useUploadEditor() {
  return useMemo(() => withUpload(withHistory(withReact(withSelectionGuard(createEditor())))), []);
}

export function useUploadEditorStatic() {
  return useSlateStatic() as ReturnType<typeof useUploadEditor>;
}
